import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import { certificateUrl, documentUrl } from '@cover/core/api/client.ts';
import type { Policy } from '@cover/core/api/types.ts';
import { date, money } from '@cover/core/format.ts';
import { Badge } from '@cover/core/ui/Badge.tsx';
import { Button } from '@cover/core/ui/Button.tsx';
import { Card, CardBody, CardHeader, Row } from '@cover/core/ui/Card.tsx';
import { Empty, Load } from '@cover/core/ui/States.tsx';
import { usePolicies } from '../api.ts';
import { PageTitle } from '../Layout.tsx';

const POLICY_STATUS: Record<Policy['status'], { label: string; tone: 'ok' | 'neutral' | 'bad' | 'info' }> = {
  active: { label: 'Active', tone: 'ok' },
  cancelled: { label: 'Cancelled', tone: 'bad' },
  lapsed: { label: 'Lapsed', tone: 'neutral' },
  external: { label: 'With another insurer', tone: 'info' },
};

export function PoliciesPage() {
  const policies = usePolicies();

  return (
    <>
      <PageTitle>Policies</PageTitle>
      <div className="space-y-5">
        <Load q={policies}>
          {(list) =>
            list.length ? (
              <>
                {list.map((p) => (
                  <PolicyCard key={p.policyNumber} policy={p} />
                ))}
              </>
            ) : (
              <Card>
                <Empty title="No policies yet">If you have cover elsewhere, add the schedule in <Link to="/documents">Documents</Link> and we’ll keep track of your renewal date.</Empty>
              </Card>
            )
          }
        </Load>

        <Card>
          <CardBody className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-mid">Receipts, certificates, schedules and anything else you’ve sent us are in Documents.</p>
            <Button asChild variant="secondary">
              <Link to="/documents" className="text-ink no-underline hover:no-underline">
                Go to documents
              </Link>
            </Button>
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function PolicyCard({ policy: p }: { policy: Policy }) {
  const status = POLICY_STATUS[p.status];
  return (
    <Card>
      <CardHeader title={p.productName ?? p.product} description={p.insurer} action={<Badge tone={status.tone}>{status.label}</Badge>} />
      <CardBody>
        <dl className="divide-y divide-line">
          <Row label="Policy number">
            <span className="font-mono">{p.policyNumber}</span>
          </Row>
          <Row label="Started">{date(p.startDate)}</Row>
          {p.renewalDate && <Row label="Renews">{date(p.renewalDate)}</Row>}
          {p.annualPremiumPence != null && <Row label="Premium">{money(p.annualPremiumPence)} a year</Row>}
        </dl>
        {p.covers && p.covers.length > 0 && (
          <>
            <h3 className="mb-1 mt-4 text-sm font-semibold">What’s covered</h3>
            <dl className="divide-y divide-line">
              {p.covers.map((c) => (
                <Row key={c.name} label={c.name}>
                  {c.limit}
                </Row>
              ))}
            </dl>
          </>
        )}
        {p.source === 'uploaded_schedule' && <p className="mt-3 text-sm text-mid">Read from a schedule you sent us.</p>}
        {p.source === 'purchased' && (
          <div className="mt-4 flex flex-wrap gap-x-6">
            {/* The certificate PDF, or the certificate endpoint for a policy without one. */}
            {(p.status === 'active' || p.certificateDocumentId) && (
              <a href={p.certificateDocumentId ? documentUrl(p.certificateDocumentId!) : certificateUrl(p.policyNumber)} target="_blank" rel="noopener" className="inline-flex min-h-11 items-center gap-2 font-semibold">
                <Download className="h-4 w-4" aria-hidden /> Certificate of insurance
              </a>
            )}
            {p.documentId && p.documentId !== p.certificateDocumentId && (
              <a href={documentUrl(p.documentId)} target="_blank" rel="noopener" className="inline-flex min-h-11 items-center gap-2 font-semibold">
                <Download className="h-4 w-4" aria-hidden /> Policy schedule
              </a>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
